import { get, patch, post } from '@/api/fetch';
import { LoadTrackerEventName, initialLoadTracker } from '@/helpers/Monitoring/DataDogMonitor';
import { UserAreaOfActivity, UserSalutation } from '@/types';
import { captureError } from '@smg-real-estate/browser-rum';

export async function toggleMfaRequirement(userId: string, state: boolean): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/toggle-mfa-requirement`;
  try {
    const requestBody = JSON.stringify({ userId, state });
    const response = await post(url, requestBody);
    return response.status === 204;
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));

    return false;
  }
}

export async function resetMfaEnrollments(userId: string): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/reset-mfa-enrollments`;
  try {
    const requestBody = JSON.stringify({ userId });
    const response = await post(url, requestBody);
    return response.status === 204;
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));

    return false;
  }
}

export async function sendMFAEnrollmentEmail(user_id: string, email: string): Promise<void> {
  const requestBody = JSON.stringify({ user_id, email, send_mail: true });
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/request-enrollment-email`;
  try {
    await post(url, requestBody);
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));
  }
}

export async function verifyCurrentUserEmail(user_id: string): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/users/${encodeURIComponent(user_id)}/verify-email?tenant=ubm`;
  try {
    await post(url);
    return true;
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));
    return false;
  }
}

export async function canUserChangeCredentials(
  user_id: string,
): Promise<{ can_change_email?: boolean; can_change_password?: boolean }> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/users/${encodeURIComponent(user_id)}/canUserChangeCredentials?tenant=ubm`;
  try {
    const response = await get(url);

    if (response.status === 200) {
      return (await response.json()).data as {
        can_change_email?: boolean;
        can_change_password?: boolean;
      };
    }
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));
  }
  return {};
}

export interface UserDetailsUpdateData {
  given_name: string;
  family_name: string;
  phone_number?: string;
  salutation?: UserSalutation;
  area_of_activity?: UserAreaOfActivity;
  role?: string;
}

export async function updateUserDetails(
  userId: string,
  userData: UserDetailsUpdateData,
): Promise<boolean> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/users/${encodeURIComponent(userId)}?tenant=ubm`;
  try {
    const response = await patch(url, JSON.stringify(userData));
    return response.status === 200;
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));

    return false;
  }
}

export interface UserDetails {
  language: string;
  userId: string;
  email: string;
  firstName: string;
  familyName: string;
  requiredMFA: boolean;
  exemptFromMFA: boolean;
  enrolledInMFA: boolean;
  salutation?: UserSalutation;
  phoneNumber?: string;
  areaOfActivity?: UserAreaOfActivity[];
  mfaEnrollmentDateUtc?: string;
}

export async function fetchUserDetails(userId: string): Promise<UserDetails | undefined> {
  const url = `${import.meta.env.VUE_APP_ACCOUNT_MANAGEMENT_API_BASE_URL}/users/${encodeURIComponent(userId)}?tenant=ubm`;
  try {
    initialLoadTracker.trackEvent(LoadTrackerEventName.getUserDetails);
    const response = await get(url);
    initialLoadTracker.endEvent(LoadTrackerEventName.getUserDetails);
    if (response.status === 200) {
      return response.json();
    }
  } catch (error) {
    captureError(new AccountManagementApiError(url, error.message));

    return undefined;
  }
}

export class AccountManagementApiError extends Error {
  endpoint: string;

  constructor(endpoint: string, message: string) {
    super(`Unexpected error happened: ${message}`);

    this.name = 'AccountManagementApiError';
    this.endpoint = endpoint;
  }
}
